import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Paper,
  Breadcrumbs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Grid,
} from '@mui/material';
import { useNotification } from '../../../components/Notification';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  NavigateNext as NavigateNextIcon,
  Update as UpdateIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import Table from '../../../components/Table';
import Input from '../../../components/Input';
import Select from '../../../components/Select';
import AddLicenseUnitModal from './AddLicenseUnitModal';
import UpdateStatusModal from './UpdateStatusModal';
import { hasPermission } from '../../../utils/permissions';
import { SUBORDINATE_LEVELS } from '../../../constants';
import {
  fetchInit,
  fetchUnits,
  fetchUnitById,
  addUnit,
  updateUnit,
  updateUnitStatus,
  selectLicenseUnits,
  selectUnitLevels,
  selectUnitStatuses,
  selectVerifyModes,
  selectAvailableLicenses,
  selectLicenseUnitsLoading,
  selectLicenseUnitsError,
  selectLicenseUnitsSuccessMessage,
  selectLicenseUnitsTotal,
  selectLicenseUnitsPage,
  selectLicenseUnitsPageSize,
  selectLicenseUnitsSortColumn,
  selectLicenseUnitsSortDirection,
  selectLicenseUnitsFilterName,
  selectLicenseUnitsFilterLevel,
  selectLicenseUnitsFilterStatus,
  selectLicenseUnitsFilterLicense,
  selectSelectedUnit,
  setPage,
  setPageSize,
  setSortColumn,
  setSortDirection,
  setFilterName,
  setFilterLevel,
  setFilterStatus,
  setFilterLicense,
  deleteUnit,
  bulkDeleteUnits,
  clearError,
  clearSuccessMessage,
  clearSelectedUnit,
} from './slice';
import './LicenseUnits.css';

function LicenseUnits() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { showNotification } = useNotification();

  const units = useSelector(selectLicenseUnits);
  const unitLevels = useSelector(selectUnitLevels);
  const unitStatuses = useSelector(selectUnitStatuses);
  const verifyModes = useSelector(selectVerifyModes);
  const availableLicenses = useSelector(selectAvailableLicenses);
  const loading = useSelector(selectLicenseUnitsLoading);
  const error = useSelector(selectLicenseUnitsError);
  const successMessage = useSelector(selectLicenseUnitsSuccessMessage);
  const total = useSelector(selectLicenseUnitsTotal);
  const page = useSelector(selectLicenseUnitsPage);
  const pageSize = useSelector(selectLicenseUnitsPageSize);
  const sortColumn = useSelector(selectLicenseUnitsSortColumn);
  const sortDirection = useSelector(selectLicenseUnitsSortDirection);
  const filterName = useSelector(selectLicenseUnitsFilterName);
  const filterLevel = useSelector(selectLicenseUnitsFilterLevel);
  const filterStatus = useSelector(selectLicenseUnitsFilterStatus);
  const filterLicense = useSelector(selectLicenseUnitsFilterLicense);
  const selectedUnit = useSelector(selectSelectedUnit);

  const [selectedIds, setSelectedIds] = useState([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [unitToDelete, setUnitToDelete] = useState(null);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [unitToUpdateStatus, setUnitToUpdateStatus] = useState(null);

  // Check permissions
  const canAddUnit = hasPermission('unit', 'addUnit');
  const canEditUnit = hasPermission('unit', 'editUnit');
  const canDeleteUnit = hasPermission('unit', 'deleteUnit');

  // Fetch init data on mount
  useEffect(() => {
    dispatch(fetchInit());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchUnits({ page, pageSize, sortColumn, sortDirection, filterName, filterLevel, filterStatus, filterLicense }));
  }, [dispatch, page, pageSize, sortColumn, sortDirection, filterName, filterLevel, filterStatus, filterLicense]);

  // Show error notification
  useEffect(() => {
    if (error) {
      showNotification(error, 'error');
      dispatch(clearError());
    }
  }, [error, dispatch, showNotification]);

  // Show success notification
  useEffect(() => {
    if (successMessage) {
      showNotification(successMessage, 'success');
      dispatch(clearSuccessMessage());
      dispatch(fetchUnits({ page, pageSize, sortColumn, sortDirection, filterName, filterLevel, filterStatus, filterLicense }));
    }
  }, [successMessage, dispatch, showNotification, page, pageSize, sortColumn, sortDirection, filterName, filterLevel, filterStatus, filterLicense]);

  // Define table columns
  const columns = [
    {
      key: 'code',
      label: t('licenseUnits.table.code'),
    },
    {
      key: 'name',
      label: t('licenseUnits.table.name'),
    },
    {
      key: 'level',
      label: t('licenseUnits.table.level'),
      render: (value) => {
        const levelObj = unitLevels.find((level) => level.id === value);
        const levelName = levelObj ? levelObj.name : '-';

        return (
          <Box
            component="span"
            sx={{
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              fontSize: '0.875rem',
              fontWeight: 500,
              backgroundColor: levelObj ? '#f3e5f5' : '#e2e8f0',
              color: levelObj ? '#6a1b9a' : '#64748b',
            }}
          >
            {levelName}
          </Box>
        );
      },
    },
    {
      key: 'licenseCode',
      label: t('licenseUnits.table.license'),
      render: (value) => value || '-',
    },
    {
      key: 'licenseIssueDate',
      label: t('licenseUnits.table.issueDate'),
      render: (value) => {
        if (!value) return '-';
        const date = new Date(value);
        return date.toLocaleDateString('vi-VN');
      },
    },
    {
      key: 'licenseExpiryDate',
      label: t('licenseUnits.table.expiryDate'),
      render: (value) => {
        if (!value) return '-';
        const date = new Date(value);
        const isExpired = date < new Date();
        return (
          <Box
            component="span"
            sx={{
              color: isExpired ? '#c62828' : 'inherit',
              fontWeight: isExpired ? 500 : 'normal',
            }}
          >
            {date.toLocaleDateString('vi-VN')}
          </Box>
        );
      },
    },
    {
      key: 'status',
      label: t('licenseUnits.table.status'),
      render: (value) => {
        const statusObj = unitStatuses.find((s) => s.id === value);
        const statusName = statusObj ? statusObj.name : '-';

        // Color based on status: Active (2) = green, Waiting (1) = yellow, Stop (0) = red
        const getStatusColor = (statusValue) => {
          switch (statusValue) {
            case 2:
              return { bg: '#e8f5e9', color: '#2e7d32' };
            case 1:
              return { bg: '#fff3e0', color: '#e65100' };
            case 0:
              return { bg: '#ffebee', color: '#c62828' };
            default:
              return { bg: '#e2e8f0', color: '#64748b' };
          }
        };

        const colors = getStatusColor(value);

        return (
          <Box
            component="span"
            sx={{
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              fontSize: '0.875rem',
              fontWeight: 500,
              backgroundColor: colors.bg,
              color: colors.color,
            }}
          >
            {statusName}
          </Box>
        );
      },
    },
    {
      key: 'location',
      label: t('licenseUnits.table.location'),
    },
  ];

  // Handle edit action
  const handleEdit = (row) => {
    dispatch(fetchUnitById(row.id));
    setShowEditDialog(true);
  };

  // Handle update status action
  const handleUpdateStatus = (row) => {
    setUnitToUpdateStatus(row);
    setShowStatusDialog(true);
  };

  // Handle delete action
  const handleDelete = (row) => {
    setUnitToDelete(row);
    setShowDeleteDialog(true);
  };

  // Confirm delete action
  const handleConfirmDelete = () => {
    if (unitToDelete?.id) {
      dispatch(deleteUnit(unitToDelete.id));
    }
    setShowDeleteDialog(false);
    setUnitToDelete(null);
  };

  // Cancel delete action
  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
    setUnitToDelete(null);
  };

  // Handle bulk delete
  const handleBulkDelete = () => {
    setShowBulkDeleteDialog(true);
  };

  // Confirm bulk delete action
  const handleConfirmBulkDelete = () => {
    dispatch(bulkDeleteUnits(selectedIds));
    setShowBulkDeleteDialog(false);
    setSelectedIds([]);
  };

  // Cancel bulk delete action
  const handleCancelBulkDelete = () => {
    setShowBulkDeleteDialog(false);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    dispatch(setPage(newPage));
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    dispatch(setPageSize(newPageSize));
  };

  // Handle sort change
  const handleSortChange = (column, direction) => {
    dispatch(setSortColumn(column));
    dispatch(setSortDirection(direction));
  };

  // Handle filter change
  const handleFilterChange = (event) => {
    dispatch(setFilterName(event.target.value));
  };

  // Handle level filter change
  const handleFilterLevelChange = (event) => {
    dispatch(setFilterLevel(event.target.value));
  };

  // Handle status filter change
  const handleFilterStatusChange = (event) => {
    dispatch(setFilterStatus(event.target.value));
  };

  // Handle license filter change
  const handleFilterLicenseChange = (event) => {
    dispatch(setFilterLicense(event.target.value));
  };

  // Handle add unit
  const handleAddUnit = () => {
    dispatch(clearSelectedUnit());
    setShowAddDialog(true);
  };

  // Handle close add unit dialog
  const handleCloseAddUnit = () => {
    setShowAddDialog(false);
    dispatch(clearSelectedUnit());
  };

  // Confirm add unit
  const handleConfirmAddUnit = (unitData) => {
    dispatch(addUnit(unitData));
    setShowAddDialog(false);
  };

  // Handle close edit unit dialog
  const handleCloseEditUnit = () => {
    setShowEditDialog(false);
    dispatch(clearSelectedUnit());
  };

  // Confirm edit unit
  const handleConfirmEditUnit = (unitData) => {
    dispatch(updateUnit(unitData));
    setShowEditDialog(false);
  };

  // Handle close status dialog
  const handleCloseStatusDialog = () => {
    setShowStatusDialog(false);
    setUnitToUpdateStatus(null);
  };

  // Confirm update status
  const handleConfirmUpdateStatus = (data) => {
    dispatch(updateUnitStatus(data));
    setShowStatusDialog(false);
    setUnitToUpdateStatus(null);
  };

  // Custom actions for table
  const customActions = canEditUnit
    ? [
        {
          icon: <UpdateIcon fontSize="small" />,
          label: t('licenseUnits.updateStatus.button'),
          onClick: handleUpdateStatus,
          color: 'primary',
        },
      ]
    : [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header Section */}
      <Box sx={{ px: 3, pt: 3, pb: 2, backgroundColor: '#ffffff' }}>
        {/* Breadcrumbs */}
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AssignmentIcon fontSize="small" />
            <Typography variant="body2">{t('licenseUnits.breadcrumb.licenseManagement')}</Typography>
          </Box>
          <Typography variant="body2" color="text.primary">
            {t('licenseUnits.breadcrumb.units')}
          </Typography>
        </Breadcrumbs>

        {/* Page Title and Action Buttons */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Typography variant="h5" component="h1" fontWeight="bold" sx={{ textAlign: 'left' }}>
            {t('licenseUnits.title')}
          </Typography>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            {canAddUnit && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddUnit}
                id="addUnit"
                name="addUnit"
              >
                {t('licenseUnits.addUnit')}
              </Button>
            )}
            {canDeleteUnit && (
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleBulkDelete}
                disabled={selectedIds.length === 0}
                id="deleteSelected"
                name="deleteSelected"
              >
                {t('licenseUnits.deleteSelected')}
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      {/* Main Content */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 0,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          backgroundColor: '#ffffff',
          mt: 2,
        }}
      >
        {/* Filter Section */}
        <Box sx={{ px: 3, pt: 2, pb: 2, borderBottom: '1px solid #e5e7eb' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Input
                label={t('licenseUnits.filterByName')}
                value={filterName}
                onChange={handleFilterChange}
                placeholder={t('licenseUnits.filterByNamePlaceholder')}
                id="filterName"
                name="filterName"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Select
                label={t('licenseUnits.filterByLevel')}
                value={filterLevel}
                onChange={handleFilterLevelChange}
                options={[{ id: '', name: t('licenseUnits.allLevels') }, ...unitLevels]}
                id="filterLevel"
                name="filterLevel"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Select
                label={t('licenseUnits.filterByStatus')}
                value={filterStatus}
                onChange={handleFilterStatusChange}
                options={[{ id: '', name: t('licenseUnits.allStatuses') }, ...unitStatuses]}
                id="filterStatus"
                name="filterStatus"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Input
                label={t('licenseUnits.filterByLicense')}
                value={filterLicense}
                onChange={handleFilterLicenseChange}
                placeholder={t('licenseUnits.filterByLicensePlaceholder')}
                id="filterLicense"
                name="filterLicense"
              />
            </Grid>
          </Grid>
        </Box>

        {/* Table */}
        <Box sx={{ flex: 1, overflow: 'auto', px: 3, pt: 2, pb: 3, backgroundColor: '#ffffff' }}>
          <Table
            id="license-units-table"
            columns={columns}
            data={units}
            selectable={true}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            onEdit={canEditUnit ? handleEdit : null}
            onDelete={canDeleteUnit ? handleDelete : null}
            customActions={customActions}
            loading={loading}
            emptyMessage={t('licenseUnits.noData')}
            idField="id"
            pagination={true}
            page={page}
            pageSize={pageSize}
            totalItems={total}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            pageSizeOptions={[5, 10, 20, 50, 100]}
            sortable={true}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSortChange={handleSortChange}
            sortableColumns={['code', 'name', 'level', 'licenseCode', 'status', 'location']}
          />
        </Box>
      </Paper>

      {/* Confirm Delete Dialog */}
      <Dialog open={showDeleteDialog} onClose={handleCancelDelete} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'error.main',
          }}
        >
          <DeleteIcon />
          {t('licenseUnits.deleteDialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('licenseUnits.deleteDialog.message', {
              name: unitToDelete?.name || '',
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelDelete} variant="outlined" id="cancelDelete" name="cancelDelete">
            {t('licenseUnits.deleteDialog.cancel')}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            id="confirmDelete"
            name="confirmDelete"
          >
            {t('licenseUnits.deleteDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Bulk Delete Dialog */}
      <Dialog open={showBulkDeleteDialog} onClose={handleCancelBulkDelete} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'error.main',
          }}
        >
          <DeleteIcon />
          {t('licenseUnits.bulkDeleteDialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('licenseUnits.bulkDeleteDialog.message', {
              count: selectedIds.length,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCancelBulkDelete}
            variant="outlined"
            id="cancelBulkDelete"
            name="cancelBulkDelete"
          >
            {t('licenseUnits.bulkDeleteDialog.cancel')}
          </Button>
          <Button
            onClick={handleConfirmBulkDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            id="confirmBulkDelete"
            name="confirmBulkDelete"
          >
            {t('licenseUnits.bulkDeleteDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Unit Modal */}
      <AddLicenseUnitModal
        open={showAddDialog}
        onClose={handleCloseAddUnit}
        onConfirm={handleConfirmAddUnit}
        unitLevels={unitLevels}
        unitStatuses={unitStatuses}
        verifyModes={verifyModes}
        availableLicenses={availableLicenses}
      />

      {/* Edit Unit Modal */}
      <AddLicenseUnitModal
        open={showEditDialog}
        onClose={handleCloseEditUnit}
        onConfirm={handleConfirmEditUnit}
        unitLevels={unitLevels}
        unitStatuses={unitStatuses}
        verifyModes={verifyModes}
        availableLicenses={availableLicenses}
        unitData={selectedUnit}
        editMode={true}
      />

      {/* Update Status Modal */}
      <UpdateStatusModal
        open={showStatusDialog}
        onClose={handleCloseStatusDialog}
        onConfirm={handleConfirmUpdateStatus}
        unitStatuses={unitStatuses}
        unitData={unitToUpdateStatus}
      />
    </Box>
  );
}

export default LicenseUnits;
